import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Dialog, DialogHeader, DialogTitle, DialogContent } from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { ArrowLeft, X, FileText, CheckCircle2, XCircle, Loader2, Merge } from 'lucide-react';
import { cn } from '../../../../../lib/utils';
import { useProductionData } from '../../../../../redux/ProductionData/hooks/useProductionData';
import axiosConfig from '../../../../../redux/ProductionData/axiosConfig';
import get from 'lodash/get';
import { SpecificationDocument } from '@/features/project/types';

/** Must match `limit` in ProductionData saga `fetchAssemblyData` (getAllProductions). */
const ASSEMBLY_FETCH_BATCH_SIZE = 20;

interface DocumentItem {
    id: string;
    docId: number | null;
    name: string;
    type: string;
    fileUrl?: string;
    fileName?: string;
    category: string;
    status: 'approved' | 'pending' | null;
}

interface GenerateProductionReportModalProps {
    isOpen: boolean;
    onClose: () => void;
    projectName?: string;
    jobNumber?: string;
    projectId?: number;
    selectedPipe?: any;
    pipeSizesData?: any[];
}

export const GenerateProductionReportModal: React.FC<GenerateProductionReportModalProps> = ({
    isOpen,
    onClose,
    projectName,
    jobNumber,
    projectId,
    selectedPipe,
    pipeSizesData = [],
}) => {
    const { generateProjectProductionSheetReport, projectProductionSheetReportLoading, projectProductionSheetReportSuccess, clearProjectProductionSheetReport } = useProductionData();
    const [email, setEmail] = useState<string>('');
    const [selectedPipeSize, setSelectedPipeSize] = useState<string>('');
    const [reportGenerationLevel, setReportGenerationLevel] = useState<'assembly'>('assembly');
    const [selectedAssemblies, setSelectedAssemblies] = useState<string[]>([]);
    const [assemblies, setAssemblies] = useState<any[]>([]);
    const [isLoadingAssemblies, setIsLoadingAssemblies] = useState<boolean>(false);
    const [selectedDocIds, setSelectedDocIds] = useState<(number | null)[]>([]);
    const [specifications, setSpecifications] = useState<SpecificationDocument[]>([]);
    /** Progress while paging through getAllProductions (batch size matches saga `limit`) */
    const [assemblyFetchProgress, setAssemblyFetchProgress] = useState<{
        loaded: number;
        total: number | null;
    } | null>(null);





    useEffect(() => {

        if (selectedPipe?.documentsData?.documents && Array.isArray(selectedPipe.documentsData.documents)) {
            const newSpecifications: SpecificationDocument[] = selectedPipe.documentsData.documents.filter((doc: any) => doc.type === 'WPS' || doc.type === 'DRAWING' || doc.type === 'INSPECTION_PROCEDURE').map((doc: any) => ({
                id: doc.docRefId?.toString(),
                fileName: doc.fileName || '',
                type: doc.type as SpecificationDocument['type'],
                pipeId: selectedPipe?.pipeId?.toString(),
                projectId: selectedPipe?.projectId,
                projectTypeId: selectedPipe?.projectTypeId,
                pipeOrder: selectedPipe?.pipeOrder || 0,
                fileUrl: doc.fileUrl,
                s3Key: doc.s3Key,
                uploadedBy: doc.uploadedBy,
                uploadDate: doc.uploadDate,
                version: doc.version || 1,
                details: doc.details || {},
                files: doc.files || [],
                docRefId: doc.docRefId || 0
            }));

            console.log('newSpecifications', newSpecifications);

            setSpecifications(newSpecifications);
        } else {
            // Use mock specifications when no documents data is available
        }
    }, [selectedPipe]);
    // Track if we've already fetched for this pipe size to prevent infinite loops
    const fetchedPipeIdRef = useRef<string | null>(null);

    // Initialize selected pipe size from selectedPipe prop
    useEffect(() => {
        if (selectedPipe && selectedPipe.pipeId) {
            const pipeIdStr = selectedPipe.pipeId.toString();
            if (pipeIdStr !== selectedPipeSize) {
                setSelectedPipeSize(pipeIdStr);
                fetchedPipeIdRef.current = null; // Reset fetch tracking when pipe changes
            }
        }
    }, [selectedPipe?.pipeId]); // Only depend on pipeId, not the whole object

    useEffect(() => {
        if (!isOpen || !projectId || !selectedPipeSize) {
            return;
        }
        let cancelled = false;
        const loadAssemblies = async () => {
            setIsLoadingAssemblies(true);
            setAssemblyFetchProgress({ loaded: 0, total: null });
            try {
                const allProductions: any[] = [];
                let totalCount: number | null = null;
                let pageIndex = 0;

                while (!cancelled) {
                    const params = new URLSearchParams({
                        project_id: String(projectId),
                        pipe_id: String(selectedPipeSize),
                        offset: String(pageIndex),
                        limit: String(ASSEMBLY_FETCH_BATCH_SIZE),
                    });
                    const res = await axiosConfig.get(
                        `/productions/getAllProductions?${params.toString()}`
                    );
                    if (cancelled) return;

                    const data = get(res, ['data', 'data'], {});
                    const batch = Array.isArray(data?.productions) ? data.productions : [];
                    if (typeof data?.totalCount === 'number') {
                        totalCount = data.totalCount;
                    }

                    allProductions.push(...batch);
                    setAssemblyFetchProgress({
                        loaded: allProductions.length,
                        total: totalCount,
                    });

                    if (batch.length === 0) break;
                    if (totalCount != null && allProductions.length >= totalCount) break;
                    if (batch.length < ASSEMBLY_FETCH_BATCH_SIZE) break;
                    pageIndex += 1;
                }

                if (cancelled) return;

                const mapped = allProductions.map((assembly: any, index: number) => ({
                    ...assembly,
                    assemblyNumber:
                        assembly.assemblyNumber ||
                        assembly.assembly_number ||
                        (assembly.prefix ? `${assembly.prefix}-${index + 1}` : `Assy-${index + 1}`),
                }));
                setAssemblies(mapped);
                const approvedAssemblies = mapped.filter((a: any) => {
                    const docs = a?.productionSheet?.mergedProductionSheetReportDocs;
                    if (!docs || !Array.isArray(docs) || docs.length === 0) {
                        return false;
                    }
                    const sortedDocs = [...docs].sort((a: any, b: any) => {
                        const versionA = a.details?.versionNumber || 0;
                        const versionB = b.details?.versionNumber || 0;
                        return versionB - versionA;
                    });
                    const latestDoc = sortedDocs[0];
                    return latestDoc?.approvalStatus === 'approved';
                });
                const approvedNumbers = approvedAssemblies.map((a: any) => {
                    return a.assembly_number || a.assemblyNumber || `Assembly-${a.production_id}`;
                });
                if (approvedNumbers.length > 0) {
                    setSelectedAssemblies(approvedNumbers);
                }
            } catch {
                if (!cancelled) {
                    setAssemblies([]);
                }
            } finally {
                if (!cancelled) {
                    setIsLoadingAssemblies(false);
                    setAssemblyFetchProgress(null);
                }
            }
        };
        loadAssemblies();
        return () => {
            cancelled = true;
        };
    }, [isOpen, projectId, selectedPipeSize]);


    // Collect all documents from the from sepcteion for selcted pipe
    const allDocuments = useMemo(() => {
        const documents: DocumentItem[] = [];
        const specList = Array.isArray(specifications) ? specifications : (specifications ? [specifications] : []);

        specList.forEach((spec: any) => {
            const type: string = spec.type || '';
            const category =
                type === 'WPS' ? 'WPS' :
                    type === 'DRAWING' ? 'Drawing' :
                        type === 'INSPECTION_PROCEDURE' ? 'Inspection Procedure' :
                            type;

            const files = Array.isArray(spec.files) ? spec.files : [];
            files.forEach((file: any) => {
                if (file && (file.fileUrl || file.fileName)) {
                    documents.push({
                        id: `${type}-${file.docId ?? spec.id}`,
                        docId: file.docId ?? null,
                        name: file.fileName || `${category} Document`,
                        type,
                        fileUrl: file.fileUrl,
                        fileName: file.fileName,
                        category,
                        status: file.approvalStatus === 'approved' ? 'approved' : file.approvalStatus === 'rejected' ? 'pending' : null
                    });
                }
            });
        });

        return documents;
    }, [specifications]);



    // Format project sub type for display
    const formatSubType = (type: string): string => {
        switch (type) {
            case 'buckleArrestor': return 'Buckle Arrestor';
            case 'pipeInPipe': return 'Pipe in Pipe';
            case 'bulkhead': return 'Bulk Head';
            case 'none': return '';
            default: return type ? type.charAt(0).toUpperCase() + type.slice(1) : '';
        }
    };

    // Compute header info items for display (project, job, pipe info)
    const headerInfoItems = useMemo(() => {
        const infoItems = [];

        // Add project name
        if (projectName) {
            infoItems.push({ label: 'Project', value: projectName, bgColor: 'text-blueprint-blue bg-[rgba(0,39,63,0.06)]' });
        }

        // Add job number
        if (jobNumber) {
            infoItems.push({ label: 'Job', value: jobNumber, bgColor: 'bg-cyan-100 text-cyan-800' });
        }

        // Add pipe information if available
        if (selectedPipe) {
            const size = selectedPipe.size || selectedPipe.pipeSize || '';
            const wallThickness = selectedPipe.wallThickness || '';
            const subType = selectedPipe.projectSubType || '';
            const formattedSubType = formatSubType(subType);

            if (size) {
                infoItems.push({ label: 'Size', value: `${size}" O.D.`, bgColor: 'text-blueprint-blue bg-[rgba(0,39,63,0.06)]' });
            }
            if (wallThickness) {
                infoItems.push({ label: 'Wall Thickness', value: `${wallThickness}" W.T`, bgColor: 'text-galvanizing-green bg-[rgba(31,154,58,0.08)]' });
            }
            if (formattedSubType) {
                infoItems.push({ label: 'Type', value: formattedSubType, bgColor: 'text-blueprint-blue bg-[rgba(0,39,63,0.06)]' });
            }
        }

        return infoItems;
    }, [projectName, jobNumber, selectedPipe]);
    // Check if assembly is approved
    const isAssemblyApproved = (assembly: any): boolean => {
        const docs = assembly?.productionSheet?.mergedProductionSheetReportDocs;
        if (!docs || !Array.isArray(docs) || docs.length === 0) {
            return false;
        }

        const sortedDocs = [...docs].sort((a, b) => {
            const versionA = a.details?.versionNumber || 0;
            const versionB = b.details?.versionNumber || 0;
            return versionB - versionA;
        });

        const latestDoc = sortedDocs[0];
        return latestDoc?.approvalStatus === 'approved';
    };

    // Process assemblies by approval status
    const processedAssemblies = useMemo(() => {
        const approved = assemblies.filter(a => isAssemblyApproved(a));
        const notApproved = assemblies.filter(a => !isAssemblyApproved(a));

        return {
            approved,
            notApproved,
            total: assemblies.length,
            approvedCount: approved.length,
            notApprovedCount: notApproved.length
        };
    }, [assemblies]);

    // Get selected pipe size data
    const selectedPipeSizeData = pipeSizesData.find(p => p.pipeId.toString() === selectedPipeSize);

    // Handle assembly toggle
    const handleAssemblyToggle = (assemblyNumber: string) => {
        setSelectedAssemblies(prev => {
            if (prev.includes(assemblyNumber)) {
                return prev.filter(a => a !== assemblyNumber);
            } else {
                return [...prev, assemblyNumber];
            }
        });
    };

    // Handle document approve toggle
    const handleDocApproveToggle = (docId: number | null) => {
        setSelectedDocIds(prev => {
            if (prev.includes(docId)) {
                return prev.filter(id => id !== docId);
            } else {
                return [...prev, docId];
            }
        });
    };

    // Handle select all approved
    const handleSelectAllApproved = () => {
        const approvedNumbers = processedAssemblies.approved.map(a => {
            return a.assembly_number || a.assemblyNumber || `Assembly-${a.production_id}`;
        });
        setSelectedAssemblies(approvedNumbers);
    };

    // Handle merge all not approved
    const handleMergeNotApproved = () => {
        // This would trigger a merge action for all not approved assemblies
        // For now, just select them
        const notApprovedNumbers = processedAssemblies.notApproved.map(a => {
            return a.assembly_number || a.assemblyNumber || `Assembly-${a.production_id}`;
        });
        setSelectedAssemblies(notApprovedNumbers);
        setReportGenerationLevel('assembly');
    };

    // Get selected assembly IDs
    const getSelectedAssemblyIds = (): number[] => {
        // console.log('=== getSelectedAssemblyIds Debug ===');
        // console.log('assemblies:', assemblies);
        // console.log('selectedAssemblies:', selectedAssemblies);

        const ids = assemblies
            .filter(assembly => {
                const assemblyNumber = assembly.assembly_number || assembly.assemblyNumber || `Assembly-${assembly.production_id}`;
                const isSelected = selectedAssemblies.includes(assemblyNumber);
                const isApproved = isAssemblyApproved(assembly);

                // Debug logging for selected assemblies
                if (isSelected) {
                    console.log('Selected assembly found:', {
                        assemblyNumber,
                        production_id: assembly.production_id,
                        productionId: assembly.productionId,
                        id: assembly.id,
                        isApproved,
                        hasProductionSheet: !!assembly.productionSheet,
                        assemblyKeys: Object.keys(assembly)
                    });
                }

                return isSelected && isApproved;
            })
            .map(assembly => {
                // Try multiple possible ID field names - production_id is the most common
                const id = assembly.production_id || assembly.productionId || assembly.id;
                console.log('Mapping assembly to ID:', {
                    assemblyNumber: assembly.assembly_number || assembly.assemblyNumber,
                    production_id: assembly.production_id,
                    productionId: assembly.productionId,
                    id: assembly.id,
                    finalId: id
                });
                return id;
            })
            .filter(id => {
                const isValid = id !== undefined && id !== null && id !== '';
                if (!isValid) {
                    console.warn('Filtered out invalid ID:', id);
                }
                return isValid;
            });

        console.log('Final selected assembly IDs:', ids);
        console.log('=== End Debug ===');

        return ids;
    };

    // Handle generate report
    const handleGenerateReport = () => {
        if (!projectId || !selectedPipeSize) {
            return;
        }

        // Only pass selected assembly IDs
        const selectedAssemblyIds = getSelectedAssemblyIds();

        if (selectedAssemblyIds.length === 0) {
            // console.log('selectedAssemblyIds', selectedAssemblyIds);
            // Show error or notification that assemblies must be selected
            return;
        }

        const payload: any = {
            projectId,
            pipeId: parseInt(selectedPipeSize),
            assemblyIds: selectedAssemblyIds,
            WpsDocIds: allDocuments.filter(d => d.type === 'WPS' && selectedDocIds.includes(d.docId)).map(d => d.docId),
            drawingDocIds: allDocuments.filter(d => d.type === 'DRAWING' && selectedDocIds.includes(d.docId)).map(d => d.docId),
            inspectionProcedureDocIds: allDocuments.filter(d => d.type === 'INSPECTION_PROCEDURE' && selectedDocIds.includes(d.docId)).map(d => d.docId),
            // emailAddress:email,
        };

        console.log('payload', payload);

        generateProjectProductionSheetReport(payload);
    };

    // Handle save report (same as generate)
    const handleSaveReport = () => {
        handleGenerateReport();
    };

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setSelectedAssemblies([]);
            setSelectedDocIds([]);
            setReportGenerationLevel('assembly');
            fetchedPipeIdRef.current = null; // Reset fetch tracking when modal closes
            // Clear success state when modal closes
            if (projectProductionSheetReportSuccess) {
                clearProjectProductionSheetReport();
            }
        }
    }, [isOpen, projectProductionSheetReportSuccess, clearProjectProductionSheetReport]);

    // Close modal on successful report generation
    useEffect(() => {
        if (projectProductionSheetReportSuccess && isOpen) {
            // Close modal after a delay to show success message (5 seconds)
            const timer = setTimeout(() => {
                onClose();
                clearProjectProductionSheetReport();
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [projectProductionSheetReportSuccess, isOpen, onClose]);

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="w-screen h-screen max-w-none m-0 p-0 gap-0 border-0 bg-white shadow-none rounded-none [&>button]:hidden">
                <div className="flex flex-col relative z-20 h-screen overflow-hidden">
                    <DialogHeader className="px-4 sm:px-6 py-4 border-b flex flex-row items-center justify-between bg-white relative z-10 flex-shrink-0">
                        <div className="flex items-center gap-4">
                            <Button
                                onClick={onClose}
                                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                variant="outline"
                            >
                                <ArrowLeft className="h-5 w-5 text-gray-500" />
                            </Button>
                            <div>
                                <DialogTitle className="text-lg font-bold text-gray-900">
                                    Generate Production Report
                                </DialogTitle>
                                {headerInfoItems.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {headerInfoItems.map((item, index) => (
                                            <span key={index} className={`${item.bgColor} px-2 py-1 rounded text-xs font-medium`}>
                                                {item.label}: {item.value}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </DialogHeader>

                    <div className="p-4 sm:p-6 lg:p-8 overflow-y-auto flex-1">
                        <div className="space-y-6">
                            {/* Selected Pipe Size Status */}
                            {selectedPipeSizeData && (
                                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-2 p-4">
                                    <div className="flex items-start gap-3">
                                        <div className="p-2 bg-blue-100 rounded-lg">
                                            <FileText className="h-5 w-5 text-blue-600" />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="text-base font-bold text-gray-900 mb-2">Selected Pipe Size</h3>
                                            <div className="space-y-1 text-sm">
                                                <p>
                                                    <span className="font-semibold text-gray-700">Pipe Size:</span>{' '}
                                                    <span className="text-gray-900">{selectedPipeSizeData.displayName || selectedPipeSizeData.pipeName}</span>
                                                </p>
                                                <div className="flex items-center gap-4 mt-2 flex-wrap">
                                                    {isLoadingAssemblies && assemblyFetchProgress && (
                                                        <div className="w-full text-xs text-blue-700 flex items-center gap-2 mb-1">
                                                            <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" />
                                                            <span>
                                                                Loading assemblies…{' '}
                                                                {assemblyFetchProgress.total != null
                                                                    ? `${assemblyFetchProgress.loaded} / ${assemblyFetchProgress.total}`
                                                                    : `${assemblyFetchProgress.loaded} loaded`}
                                                            </span>
                                                        </div>
                                                    )}
                                                    <div>
                                                        <span className="font-semibold text-gray-700">Total Assemblies:</span>{' '}
                                                        <span className="text-gray-900">{processedAssemblies.total}</span>
                                                    </div>
                                                    <div>
                                                        <span className="font-semibold text-green-700">Approved:</span>{' '}
                                                        <span className="text-green-600">{processedAssemblies.approvedCount}</span>
                                                    </div>
                                                    <div>
                                                        <span className="font-semibold text-orange-700">Not Approved:</span>{' '}
                                                        <span className="text-orange-600">{processedAssemblies.notApprovedCount}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}





                            {/* Specification Documents */}
                            {allDocuments.length > 0 && (
                                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-sm font-semibold text-gray-900">
                                            Specification Documents
                                            <span className="ml-2 text-xs font-normal text-gray-500">
                                                ({selectedDocIds.length} of {allDocuments.length} approved)
                                            </span>
                                        </h3>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            className="text-xs h-8 px-3"
                                            onClick={() => {
                                                if (selectedDocIds.length === allDocuments.length) {
                                                    setSelectedDocIds([]);
                                                } else {
                                                    setSelectedDocIds(allDocuments.map(d => d.docId));
                                                }
                                            }}
                                        >
                                            {selectedDocIds.length === allDocuments.length ? 'Deselect All' : 'Approve All'}
                                        </Button>
                                    </div>
                                    {(['WPS', 'DRAWING', 'INSPECTION_PROCEDURE'] as const).map((type) => {
                                        const groupDocs = allDocuments.filter(d => d.type === type);
                                        if (groupDocs.length === 0) return null;
                                        const categoryLabel = type === 'WPS' ? 'WPS' : type === 'DRAWING' ? 'Drawing' : 'Inspection Procedure';
                                        return (
                                            <div key={type} className="mb-4 last:mb-0">
                                                <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">{categoryLabel}</h4>
                                                <div className="space-y-2">
                                                    {groupDocs.map((doc) => {
                                                        const isApproved = selectedDocIds.includes(doc.docId);
                                                        return (
                                                            <div
                                                                key={doc.id}
                                                                className={cn(
                                                                    "flex items-center justify-between p-3 rounded-lg border transition-all",
                                                                    isApproved
                                                                        ? "border-green-400 bg-green-50"
                                                                        : "border-gray-200 bg-gray-50"
                                                                )}
                                                            >
                                                                <div className="flex items-center gap-3 min-w-0">
                                                                    <FileText className={cn("h-4 w-4 flex-shrink-0", isApproved ? "text-green-600" : "text-gray-400")} />
                                                                    <p className="text-sm font-medium text-gray-900 truncate">{doc.name}</p>
                                                                </div>
                                                                <Button
                                                                    size="sm"
                                                                    variant={isApproved ? "default" : "outline"}
                                                                    className={cn(
                                                                        "ml-4 flex-shrink-0 text-xs h-8 px-3",
                                                                        isApproved
                                                                            ? "bg-green-600 hover:bg-green-700 text-white border-green-600"
                                                                            : "border-gray-300 text-gray-700 hover:bg-gray-100"
                                                                    )}
                                                                    onClick={() => handleDocApproveToggle(doc.docId)}
                                                                >
                                                                    {isApproved ? (
                                                                        <><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Approved</>
                                                                    ) : (
                                                                        'Approve'
                                                                    )}
                                                                </Button>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            {/* Merge Not Approved Button */}
                            {processedAssemblies.notApprovedCount > 0 && (
                                <div className="bg-orange-50 border rounded-lg p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <p className="text-sm font-medium text-orange-900">
                                                {processedAssemblies.notApprovedCount} assembly{processedAssemblies.notApprovedCount !== 1 ? 'ies' : ''} not approved
                                            </p>
                                            <p className="text-xs text-orange-700 mt-1">
                                                Not approved assemblies cannot be selected to generate report
                                            </p>
                                        </div>

                                    </div>
                                </div>
                            )}
                            {/* Assembly Selection - Only show if assembly level is selected */}
                            {reportGenerationLevel === 'assembly' && assemblies.length > 0 && (
                                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <h3 className="text-sm font-semibold text-gray-900">
                                            Select Assemblies
                                            <span className="ml-2 text-xs font-normal text-gray-500">
                                                ({selectedAssemblies.length} of {processedAssemblies.approvedCount} approved selected)
                                            </span>
                                        </h3>
                                        <Button
                                            onClick={handleSelectAllApproved}
                                            variant="outline"
                                            size="sm"
                                            className="text-xs"
                                        >
                                            {selectedAssemblies.length === processedAssemblies.approvedCount ? 'Deselect All' : 'Select All Approved'}
                                        </Button>
                                    </div>

                                    {/* Approved Assemblies */}
                                    {processedAssemblies.approved.length > 0 && (
                                        <div className="mb-4">
                                            <h4 className="text-xs font-medium text-gray-700 mb-2">Approved Assemblies ({processedAssemblies.approvedCount})</h4>
                                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 max-h-[200px] overflow-y-auto border rounded-lg p-3 bg-green-50">
                                                {processedAssemblies.approved.map((assembly) => {
                                                    const assemblyNumber = assembly.assembly_number || assembly.assemblyNumber || `Assembly-${assembly.production_id}`;
                                                    const isSelected = selectedAssemblies.includes(assemblyNumber);

                                                    return (
                                                        <div
                                                            key={assembly.production_id || assembly.assembly_number}
                                                            onClick={() => handleAssemblyToggle(assemblyNumber)}
                                                            className={cn(
                                                                "flex items-center gap-2 p-2 border rounded cursor-pointer transition-all",
                                                                isSelected
                                                                    ? " bg-green-100"
                                                                    : " bg-white hover:bg-green-50"
                                                            )}
                                                        >
                                                            <div className="flex-shrink-0">
                                                                {isSelected ? (
                                                                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                                                                ) : (
                                                                    <div className="h-4 w-4 rounded border-2" />
                                                                )}
                                                            </div>
                                                            <span className="text-xs font-medium text-green-900 truncate flex-1">
                                                                {assemblyNumber}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Not Approved Assemblies */}
                                    {processedAssemblies.notApproved.length > 0 && (
                                        <div>
                                            <h4 className="text-xs font-medium text-gray-700 mb-2">Not Approved Assemblies ({processedAssemblies.notApprovedCount})</h4>
                                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 max-h-[200px] overflow-y-auto border rounded-lg p-3 bg-orange-50">
                                                {processedAssemblies.notApproved.map((assembly) => {
                                                    const assemblyNumber = assembly.assembly_number || assembly.assemblyNumber || `Assembly-${assembly.production_id}`;

                                                    return (
                                                        <div
                                                            key={assembly.production_id || assembly.assembly_number}
                                                            className="flex items-center gap-2 p-2 border rounded bg-white opacity-60 cursor-not-allowed"
                                                        >
                                                            <XCircle className="h-4 w-4 text-orange-400 flex-shrink-0" />
                                                            <span className="text-xs font-medium text-orange-900 truncate flex-1">
                                                                {assemblyNumber}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Success Message */}
                            {projectProductionSheetReportSuccess && (
                                <div className="flex items-start gap-3 p-4 bg-green-50 border rounded-lg">
                                    <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                                    <div className="flex-1">
                                        <div className="text-sm font-medium text-green-900 mb-1">
                                            Report generation initiated successfully!
                                        </div>
                                        <div className="text-xs text-green-700">
                                            The report is being generated in the background. This process typically takes 5-10 minutes. You can check the "Production reports" section to view the generated report once it's ready.
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex flex-col sm:flex-row justify-end gap-3 sm:gap-4 pt-6 border-t">

                                 {(
                                     <Button
                                         onClick={handleSaveReport}
                                         className="bg-[#EB1C37] hover:bg-[#EB1C37]/90 text-white px-6"
                                         disabled={projectProductionSheetReportLoading || !projectId || !selectedPipeSize}
                                     >
                                         {projectProductionSheetReportLoading ? (
                                             <>
                                                 <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                 Generating...
                                             </>
                                         ) : (
                                             <>
                                                 <FileText className="h-4 w-4 mr-2" />
                                                 Generate & Save Report
                                             </>
                                         )}
                                     </Button>
                                 )}
                             </div>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

